import { isShopifyEmbedded } from "@shopify/app-bridge-utils"
import { Observer } from "mobx-react"
import React from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { resolveActiveTabId } from "@/component/ShopifyAppNav/resolveActiveTabId"
import { THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM } from "@/pages/TrackingPage/DisplayOptions/components/themeLayoutAppWindow"
import stores from "@/stores"
import { getAppLink } from "@/utils/appBridge"

/**
 * Shopify admin 左侧的应用导航菜单。
 *
 * 用声明式的 `<s-app-nav>` 取代 App Bridge 3.x 的 `NavigationMenu.create()`：
 * 菜单项由 DOM 描述，App Bridge 读取子元素的 href / 文本后同步给 admin，
 * 因此不需要再手动持有菜单实例、也不需要在路由变化时去 set({ active })。
 *
 * 约束（来自 App Bridge 对该标签的校验，越界会在控制台告警并丢弃该项）：
 * - 直接子元素只能是 `<s-link>` 或 `<a>`，且内容必须是纯文本，不能再套元素
 * - href 只接受应用内相对路径、`app:` URL 或同源 http(s) URL
 * - 不支持多级嵌套，菜单只有一层
 * - 只有顶层应用帧的 `<s-app-nav>` 会被 App Bridge 接管；在 Max Modal 的内容 iframe 里
 *   该标签不会被升级，会连同 `<s-link>` 一起以纯文本形式漏在弹窗正文中，故内容帧不渲染
 *
 * @see https://shopify.dev/docs/api/app-home/app-bridge-web-components/app-nav
 */

/** 不进左侧菜单的 tab：Dashboard 由 admin 的应用名入口承担，其余两项不对外暴露 */
const EXCLUDED_TAB_IDS = new Set(["/", "/get-help"])

export default function ShopifyAppNav() {
  const { pathname, search } = useLocation()
  const menuT = useTranslation("menu").t

  // `<Modal variant="max" src>` / `<s-app-window>` 的内容 iframe：整个 App 被重新加载，
  // 此处若再渲染 s-app-nav，未被 App Bridge 升级的元素会把菜单项文字直接显示在弹窗里
  const isAppWindowContentFrame = new URLSearchParams(search ?? "")
    .get(THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM) === "1"

  // 与 App.tsx 里原本创建 NavigationMenu 的判定保持一致
  if (window.isFromAdmin || !isShopifyEmbedded() || isAppWindowContentFrame) {
    return null
  }

  return (
    <Observer>{() => {
      const items = stores.globalMenuStore.tabs.filter(item => !EXCLUDED_TAB_IDS.has(item.id ?? ""))
      const activeTabId = resolveActiveTabId(pathname, items.map(item => item.id ?? ""))

      return (
        <s-app-nav>
          {items.map(item => (
            <s-link
              key={item.id}
              href={getAppLink(item.id)}
              /**
               * active 只能「存在 / 不存在」，不能传布尔值：React 会把 false 渲染成
               * active="false"，而 App Bridge 读的是 hasAttribute("active")。
               *
               * 注意：当前 CDN 版本的 app-bridge.js 并不会把这个属性同步给 admin
               * （读它的 selectedMenuItemId() 是死代码），高亮实际由 admin 按 href 匹配，
               * 这里只是把意图写在 DOM 上，等官方接上时无需再改。
               */
              {...(item.id === activeTabId ? { active: "" } : {})}
            >
              {menuT(item.i18nKey)}
            </s-link>
          ))}
        </s-app-nav>
      )
    }}</Observer>
  )
}
